import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsentBanner.styles.css';

// * [COMPONENT] DPDP Act 2023 Statutory Telemetry & Cookie Consent Pop-up
// ? Gives Data Principals clear affirmative choice over analytics storage before telemetry activates
export const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    // Check existing stored consent status
    const consent = localStorage.getItem('dpdp_consent');
    if (!consent) {
      // Delay slightly for smooth entrance after quantum loader
      const timer = setTimeout(() => setIsVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for manual trigger from footer "Cookie Settings" button
  useEffect(() => {
    const handleOpenSettings = () => {
      setIsVisible(true);
    };
    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('open-cookie-settings', handleOpenSettings);
  }, []);

  const handleConsent = (status: 'granted' | 'denied') => {
    localStorage.setItem('dpdp_consent', status);
    setIsVisible(false);

    // Update Google Analytics gtag consent mode if available
    if (typeof window !== 'undefined' && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
      (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('consent', 'update', {
        analytics_storage: status,
      });
    }
  };

  if (!isVisible) return null;

  return (
    <aside className="cookie-banner-overlay" aria-label="Privacy and Cookie Consent Notice">
      <div className="cookie-banner-card glass">
        <div className="cookie-banner-header">
          <span className="cookie-icon">🛡️</span>
          <h3 className="cookie-banner-title">Privacy & Data Protection Notice</h3>
        </div>

        <p className="cookie-banner-text">
          In accordance with India&apos;s <strong>Digital Personal Data Protection Act, 2023 (DPDP)</strong>,
          we request your affirmative consent to process anonymous telemetry data for analytics and performance optimization.
          You may review our{' '}
          <Link to="/privacy" className="cookie-legal-link">Privacy Policy</Link> or change preferences anytime via the footer.
        </p>

        <div className="cookie-banner-actions">
          <button
            type="button"
            className="cookie-btn cookie-btn-accept"
            onClick={() => handleConsent('granted')}
          >
            Accept All
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-reject"
            onClick={() => handleConsent('denied')}
          >
            Reject Non-Essential
          </button>
        </div>
      </div>
    </aside>
  );
};

export default CookieConsentBanner;
